<script setup lang="ts">
    import infoIcon from '~/assets/icons/info-white.svg';

    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const { showSectionSwitchNotification } = useSwitchNotification();
    const { commitCountryLanguage } = useCountryLanguageSwitch();
    const shopwareDownToast = useState<any | null>('shopware-down-toast-el', () => null);
    const currentCountry = ref<string | null>(null);
    const initialEntryHandled = ref(false);
    const sectionSwitchToastSeen = ref(false);

    const pendingCountryChange = ref<{
        newPath: string;
        oldPath?: string;
    } | null>(null);

    const platform = usePlatformMeta();

    const { data: globalCountryConfigStory, status: globalCountryConfigStatus } =
        await useGlobalConfig<CountryConfigStoryblok>('globalcountryconfig', {});

    const countryEntries = computed<CountryEntryStoryblok[]>(
        () => globalCountryConfigStory.value?.content?.CountryList ?? []
    );

    const getCountry = (countryCode: string | null): CountryEntryStoryblok | undefined => {
        if (!countryCode) {
            return undefined;
        }

        return countryEntries.value.find((country) => country.countryId === countryCode);
    };

    const hasRequiredToastContent = (
        current: CountryEntryStoryblok | undefined,
        previous: CountryEntryStoryblok | undefined
    ): current is CountryEntryStoryblok => {
        if (!current) {
            return false;
        }

        const hasBaseContent = Boolean(
            current.countryId && current.title && current.sectionSwitchToast && current.primaryButton
        );

        if (!hasBaseContent) {
            return false;
        }

        if (previous) {
            return Boolean(previous.countryId && current.secondaryButton);
        }

        return Boolean(current.chooseDifferentCountry);
    };

    const handleCountryChange = (newPath: string, oldPath?: string) => {
        const newLocale = parseLocaleFromPath(newPath);

        if (!newLocale?.country) {
            return;
        }

        if (platform.isRoot.value === false) {
            return;
        }

        const newCountryCode = newLocale.country;

        if (!initialEntryHandled.value) {
            initialEntryHandled.value = true;
            currentCountry.value = newCountryCode;

            if (sectionSwitchToastSeen.value) {
                return;
            }

            const currentCountryEntry = getCountry(newCountryCode);

            if (!hasRequiredToastContent(currentCountryEntry, undefined)) {
                return;
            }

            showSectionSwitchNotification({
                currentCountry: currentCountryEntry,
                previousCountry: null
            });

            sessionStorage.setItem('section-switch-toast-seen', 'true');

            sectionSwitchToastSeen.value = true;

            return;
        }

        const oldLocale = oldPath ? parseLocaleFromPath(oldPath) : null;

        const previousCountryCode = oldLocale?.country ?? currentCountry.value;

        const previousLanguage = oldLocale?.language ?? null;

        if (!previousCountryCode) {
            currentCountry.value = newCountryCode;
            return;
        }

        const isCountrySwitch = previousCountryCode !== newCountryCode;

        if (!isCountrySwitch) {
            currentCountry.value = newCountryCode;
            return;
        }

        const currentCountryEntry = getCountry(newCountryCode);

        const previousCountryEntry = getCountry(previousCountryCode);

        if (!hasRequiredToastContent(currentCountryEntry, previousCountryEntry)) {
            currentCountry.value = newCountryCode;
            return;
        }

        showSectionSwitchNotification({
            currentCountry: currentCountryEntry,
            previousCountry: previousCountryEntry ?? null,

            onConfirm: async () => {
                if (!previousLanguage) {
                    return;
                }

                await commitCountryLanguage(previousCountryCode, previousLanguage);
            }
        });

        currentCountry.value = newCountryCode;
    };

    watch(
        () => router.currentRoute.value.path,
        (newPath, oldPath) => {
            const newLocale = parseLocaleFromPath(newPath);
            const oldLocale = parseLocaleFromPath(oldPath);

            if (!newLocale?.country || !newLocale.language) {
                return;
            }

            const countryChanged = newLocale.country !== oldLocale?.country;

            const languageChanged = newLocale.language !== oldLocale?.language;

            if (!countryChanged && !languageChanged) {
                return;
            }

            if (countryChanged && !languageChanged) {
                pendingCountryChange.value = null;
                handleCountryChange(newPath, oldPath);
                return;
            }

            if (!countryChanged && languageChanged) {
                pendingCountryChange.value = null;
                return;
            }

            pendingCountryChange.value = {
                newPath,
                oldPath
            };
        }
    );

    watch(
        globalCountryConfigStory,
        () => {
            if (!pendingCountryChange.value) {
                return;
            }

            const { newPath, oldPath } = pendingCountryChange.value;

            handleCountryChange(newPath, oldPath);

            pendingCountryChange.value = null;
        },
        { deep: true }
    );

    onMounted(() => {
        sectionSwitchToastSeen.value = sessionStorage.getItem('section-switch-toast-seen') === 'true';

        const locale = parseLocaleFromPath(route.path);

        if (locale?.language) {
            if (globalCountryConfigStatus.value === 'success') {
                handleCountryChange(route.path);
            } else {
                pendingCountryChange.value = {
                    newPath: route.path
                };
            }
        }

        if (route.query.shopwareDown) {
            shopwareDownToast.value = useToast().info({
                color: '#fff',
                title: t('errors.shopwareDownToastTitle'),
                message: t('errors.shopwareDownToastMessage'),
                // @ts-expect-error new attribute, therefore not yet recognized but still works
                iconUrl: infoIcon,
                animateInside: false,
                timeout: false,
                position: 'topRight',
                class: 'notification info shopware-down-toast',
                progressBar: false
            });
        }

        if (route.query.docebo_session_error) {
            useToast().info({
                color: '#fff',
                title: t('academy.doceboAuthErrorToastTitle'),
                message: t('academy.doceboAuthErrorToastMessage'),
                // @ts-expect-error new attribute, therefore not yet recognized but still works
                iconUrl: infoIcon,
                animateInside: false,
                timeout: false,
                position: 'topRight',
                class: 'notification info',
                progressBar: false
            });

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { docebo_session_error, ...remainingQuery } = route.query;
            router.replace({
                path: route.path,
                query: remainingQuery,
                hash: route.hash
            });
        }
    });
</script>

<!-- We need a template block for the component to be valid -->
<template>
    <slot />
</template>

<style lang="scss">
    @import '~/assets/scss/basics/toast-override.scss';
</style>
